import { useCallback, useMemo } from 'react';

import type { TimeFormat } from '@app/api/Branches/types';
import {
  useGetJournalSettingsQuery,
  useSetJournalSettingsMutation,
} from '@app/api/Settings';
import { useUserPrint } from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';

export interface JournalTimeFormatOption {
  label: string;
  value: TimeFormat;
}

// Формат времени журнала — настройка филиала на бэке (та же, что на странице
// Настройки журнала записей). Поповер журнала применяет её сразу, без кнопки
// сохранения: в поповере сохранять нечего. Формат приходит и уходит вместе с
// остальными настройками журнала одной моделью (us/11144): методы
// GetBranchJournalTimeFormat / SetBranchJournalTimeFormat с бэка удалены.
export const useJournalTimeFormat = () => {
  const { activeBranchId, userId, database } = useUserPrint();
  const { showNotification } = useNotification();

  const { data, isLoading, isFetching, isUninitialized, refetch } =
    useGetJournalSettingsQuery(
      {
        ActiveBranchId: activeBranchId,
        logs: { isLogged: true, userId, database },
      },
      { skip: !activeBranchId }
    );

  const [saveJournalSettings, { isLoading: isSaving }] =
    useSetJournalSettingsMutation();

  const timeFormatOptions = useMemo<JournalTimeFormatOption[]>(
    () =>
      (data?.TimeFormats ?? []).map(({ Value, Key }) => ({
        label: Value ?? '',
        value: Key,
      })),
    [data?.TimeFormats]
  );

  const timeFormat = useMemo(
    () => data?.TimeFormat ?? null,
    [data?.TimeFormat]
  );

  const isTimeFormatLoading =
    isLoading || isFetching || isUninitialized || isSaving;

  // true — формат сохранён. Сетка журнала берёт формат из ответа загрузки
  // журнала, поэтому по true вызывающий перезагружает журнал (issue/11215).
  const changeTimeFormatHandler = useCallback(
    async (value: TimeFormat): Promise<boolean> => {
      if (!value || !data) return false;

      try {
        await saveJournalSettings({
          ActiveBranchId: activeBranchId,
          data: { ...data, TimeFormat: value },
        }).unwrap();
        refetch();

        showNotification('Формат времени успешно изменен', 'success');

        return true;
      } catch (error) {
        showNotification(error);

        return false;
      }
    },
    [activeBranchId, data, refetch, saveJournalSettings, showNotification]
  );

  return {
    timeFormat,
    timeFormatOptions,
    isTimeFormatLoading,
    changeTimeFormatHandler,
  };
};
